import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { 
  Calculator, Settings, DollarSign, Percent, CreditCard, Clock, Globe,
  Plus, Trash2, Edit2, Save, X, AlertCircle, CheckCircle2, Info,
  ArrowDown, Minus, Equal, Building2, User, Loader2, RefreshCw,
  FileText, Shield, Wallet, ExternalLink
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import PayoutEstimator from '../../components/PayoutEstimator';

const TaxAndPayoutLogic = () => {
  const { apiCall } = useAuth();
  const { toast } = useToast();
  
  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState('calculator');
  
  // Calculator state
  const [calcProductValue, setCalcProductValue] = useState(10000);
  const [calcShippingCharge, setCalcShippingCharge] = useState(100);
  // Note: Logistics Cost = Shipping Charge + GST (auto-calculated, not separate input)
  // Currently added as line item workaround until Shopify Advanced plan upgrade
  const [calcPlanCode, setCalcPlanCode] = useState('Basic');
  const [calcGatewayProvider, setCalcGatewayProvider] = useState('razorpay');
  const [calcPaymentMethod, setCalcPaymentMethod] = useState('upi');
  const [calcEntityType, setCalcEntityType] = useState('individual_huf');
  // iter-230bs — Aniyor-managed vs Self-managed shipping
  const [calcShippingMode, setCalcShippingMode] = useState('aniyor');
  const [residentCalc, setResidentCalc] = useState(null);
  const [nonResidentCalc, setNonResidentCalc] = useState(null);
  const [calculating, setCalculating] = useState(false);
  
  // Commission tiers from subscription plans (SSOT)
  const [commissionTiers, setCommissionTiers] = useState([]);
  const [commissionSource, setCommissionSource] = useState('default');
  
  // Gateway providers
  const [gatewayProviders, setGatewayProviders] = useState([]);
  const [selectedProviderMethods, setSelectedProviderMethods] = useState([]);
  
  // Edit modals
  const [showTaxRuleModal, setShowTaxRuleModal] = useState(false);
  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [showGatewayModal, setShowGatewayModal] = useState(false);
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isNewItem, setIsNewItem] = useState(false);

  // Fetch configuration and commission tiers from subscription plans
  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch tax config and commission tiers in parallel
      const [configData, tiersData] = await Promise.all([
        apiCall('/api/tax-payout-config/IN'),
        apiCall('/api/tax-payout-config/commission-tiers')
      ]);
      
      setConfig(configData);
      
      // Set commission tiers from subscription plans (SSOT)
      if (tiersData?.tiers) {
        setCommissionTiers(tiersData.tiers);
        setCommissionSource(tiersData.source);
      }
      
      // Set gateway providers
      if (configData?.gateway_providers) {
        setGatewayProviders(configData.gateway_providers);
        // Set initial provider methods
        const defaultProvider = configData.gateway_providers.find(p => p.provider_code === 'razorpay') || configData.gateway_providers[0];
        if (defaultProvider) {
          setSelectedProviderMethods(defaultProvider.payment_methods || []);
        }
      }
    } catch (error) {
      console.error('Failed to fetch config:', error);
      toast({
        title: 'Error',
        description: 'Failed to load tax configuration',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [apiCall, toast]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Calculate payouts for both resident and non-resident
  // Update payment methods when gateway provider changes
  const handleProviderChange = (providerCode) => {
    setCalcGatewayProvider(providerCode);
    const provider = gatewayProviders.find(p => p.provider_code === providerCode);
    if (provider) {
      setSelectedProviderMethods(provider.payment_methods || []);
      // Reset payment method to first available
      if (provider.payment_methods?.length > 0) {
        setCalcPaymentMethod(provider.payment_methods[0].method);
      }
    }
  };

  const runCalculation = async () => {
    if (!config) return;
    
    setCalculating(true);
    try {
      // Logistics cost auto-calculates from shipping_charge + GST on backend
      // No need to pass it separately
      const calcPayload = {
        product_value: calcProductValue,
        shipping_charge: calcShippingCharge,
        logistics_cost: 0,  // Auto-calculated as shipping_charge + shipping_gst on backend
        plan_code: calcPlanCode,
        gateway_provider: calcGatewayProvider,
        payment_method: calcPaymentMethod,
        entity_type: calcEntityType,
        country_code: 'IN',
        shipping_mode: calcShippingMode,  // iter-230bs — Aniyor / Self
      };
      
      const [residentResult, nonResidentResult] = await Promise.all([
        apiCall('/api/tax-payout-config/calculate', {
          method: 'POST',
          body: JSON.stringify({ ...calcPayload, is_resident: true })
        }),
        apiCall('/api/tax-payout-config/calculate', {
          method: 'POST',
          body: JSON.stringify({ ...calcPayload, is_resident: false })
        })
      ]);
      
      setResidentCalc(residentResult);
      setNonResidentCalc(nonResidentResult);
    } catch (error) {
      console.error('Calculation error:', error);
      toast({
        title: 'Calculation Error',
        description: 'Failed to calculate payout',
        variant: 'destructive'
      });
    } finally {
      setCalculating(false);
    }
  };

  // Auto-calculate on input change
  useEffect(() => {
    if (config && calcProductValue > 0) {
      const timer = setTimeout(() => {
        runCalculation();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [calcProductValue, calcShippingCharge, calcPlanCode, calcGatewayProvider, calcPaymentMethod, calcEntityType, calcShippingMode, config]);

  // Save configuration
  const handleSaveConfig = async () => {
    if (!config) return;
    
    setSaving(true);
    try {
      await apiCall(`/api/tax-payout-config/${config.country_code}`, {
        method: 'PUT',
        body: JSON.stringify({
          tax_rules: config.tax_rules,
          commission_tiers: config.commission_tiers,
          gateway_providers: config.gateway_providers,
          gateway_charges: config.gateway_charges,
          payout_rules: config.payout_rules
        })
      });
      
      toast({
        title: 'Success',
        description: 'Tax & Payout configuration saved successfully'
      });
      setEditMode(false);
      runCalculation(); // Recalculate with new values
    } catch (error) {
      console.error('Save error:', error);
      toast({
        title: 'Error',
        description: 'Failed to save configuration',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  // Tax Rule handlers
  const handleEditTaxRule = (rule, isNew = false) => {
    setEditingItem(isNew ? {
      key: '',
      name: '',
      description: '',
      applied_on: 'gross_sales',
      resident_rate: 0,
      non_resident_rate: 0,
      threshold: 0,
      threshold_by_entity: { individual_huf: 0, company_llp: 0, partnership: 0 },
      is_deductible: true,
      is_active: true,
      section_reference: '',
      notes: ''
    } : { ...rule });
    setIsNewItem(isNew);
    setShowTaxRuleModal(true);
  };

  const handleSaveTaxRule = () => {
    if (!editingItem) return;
    
    const newRules = isNewItem
      ? [...config.tax_rules, editingItem]
      : config.tax_rules.map(r => r.key === editingItem.key ? editingItem : r);
    
    setConfig({ ...config, tax_rules: newRules });
    setShowTaxRuleModal(false);
    setEditingItem(null);
  };

  const handleDeleteTaxRule = (key) => {
    setConfig({
      ...config,
      tax_rules: config.tax_rules.filter(r => r.key !== key)
    });
  };

  // Commission handlers
  const handleEditCommission = (tier, isNew = false) => {
    setEditingItem(isNew ? {
      plan_code: '',
      plan_name: '',
      rate: 15,
      is_active: true
    } : { ...tier });
    setIsNewItem(isNew);
    setShowCommissionModal(true);
  };

  const handleSaveCommission = () => {
    if (!editingItem) return;
    
    const newTiers = isNewItem
      ? [...config.commission_tiers, editingItem]
      : config.commission_tiers.map(t => t.plan_code === editingItem.plan_code ? editingItem : t);
    
    setConfig({ ...config, commission_tiers: newTiers });
    setShowCommissionModal(false);
    setEditingItem(null);
  };

  const handleDeleteCommission = (planCode) => {
    setConfig({
      ...config,
      commission_tiers: config.commission_tiers.filter(t => t.plan_code !== planCode)
    });
  };

  // Gateway handlers
  const handleEditGateway = (charge, isNew = false) => {
    setEditingItem(isNew ? {
      method: '',
      label: '',
      percent_fee: 0,
      fixed_fee: 0,
      gst_applicable: true,
      is_active: true
    } : { ...charge });
    setIsNewItem(isNew);
    setShowGatewayModal(true);
  };

  const handleSaveGateway = () => {
    if (!editingItem) return;
    
    const newCharges = isNewItem
      ? [...config.gateway_charges, editingItem]
      : config.gateway_charges.map(c => c.method === editingItem.method ? editingItem : c);
    
    setConfig({ ...config, gateway_charges: newCharges });
    setShowGatewayModal(false);
    setEditingItem(null);
  };

  const handleDeleteGateway = (method) => {
    setConfig({
      ...config,
      gateway_charges: config.gateway_charges.filter(c => c.method !== method)
    });
  };

  // Provider handlers
  const handleSaveProvider = () => {
    if (!editingItem) return;
    
    const newProviders = isNewItem
      ? [...(config.gateway_providers || []), editingItem]
      : (config.gateway_providers || []).map(p => 
          p.provider_code === editingItem.provider_code ? editingItem : p
        );
    
    setConfig({ ...config, gateway_providers: newProviders });
    setGatewayProviders(newProviders);
    setShowProviderModal(false);
    setEditingItem(null);
  };

  const handleDeleteProvider = (providerCode) => {
    const newProviders = (config.gateway_providers || []).filter(p => p.provider_code !== providerCode);
    setConfig({ ...config, gateway_providers: newProviders });
    setGatewayProviders(newProviders);
  };

  const handleAddPaymentMethod = () => {
    if (!editingItem) return;
    setEditingItem({
      ...editingItem,
      payment_methods: [
        ...(editingItem.payment_methods || []),
        { method: '', label: '', percent_fee: 0, fixed_fee: 0, is_active: true }
      ]
    });
  };

  const handleUpdatePaymentMethod = (index, field, value) => {
    if (!editingItem) return;
    const updatedMethods = [...(editingItem.payment_methods || [])];
    updatedMethods[index] = { ...updatedMethods[index], [field]: value };
    setEditingItem({ ...editingItem, payment_methods: updatedMethods });
  };

  const handleRemovePaymentMethod = (index) => {
    if (!editingItem) return;
    const updatedMethods = (editingItem.payment_methods || []).filter((_, i) => i !== index);
    setEditingItem({ ...editingItem, payment_methods: updatedMethods });
  };

  // Format currency
  const formatCurrency = (amount) => {
    return `₹${amount?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}`;
  };

  // Payout Calculator Card Component
  const PayoutCalculatorCard = ({ title, data, isResident, icon: Icon }) => {
    if (!data) return (
      <Card className="flex-1">
        <CardContent className="pt-6 flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );

    return (
      <Card className={`flex-1 border-2 ${isResident ? 'border-blue-200 bg-gradient-to-br from-blue-50 to-white' : 'border-purple-200 bg-gradient-to-br from-purple-50 to-white'}`}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Icon className={`h-5 w-5 ${isResident ? 'text-blue-600' : 'text-purple-600'}`} />
              {title}
            </CardTitle>
            <Badge className={isResident ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}>
              {isResident ? 'Resident' : 'Non-Resident'}
            </Badge>
          </div>
          <CardDescription>
            {isResident ? 'Indian tax resident vendor' : 'Foreign/NRI vendor'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Gross Breakdown */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Customer Pays</p>
            <div className="flex items-center justify-between p-2 bg-green-50 rounded text-sm">
              <span className="text-green-700">Product Value</span>
              <span className="text-green-600 font-medium">{formatCurrency(data.product_value)}</span>
            </div>
            {data.shipping_charge > 0 && (
              <>
                <div className="flex items-center justify-between p-2 bg-green-50 rounded text-sm">
                  <span className="text-green-700">Shipping Charge</span>
                  <span className="text-green-600 font-medium">{formatCurrency(data.shipping_charge)}</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-green-50 rounded text-sm">
                  <span className="text-green-700">GST on Shipping (18%)</span>
                  <span className="text-green-600 font-medium">{formatCurrency(data.shipping_gst)}</span>
                </div>
              </>
            )}
            <div className="flex items-center justify-between p-3 bg-green-100 rounded-lg border border-green-200">
              <span className="text-sm font-semibold text-green-800">Total Gross</span>
              <span className="font-bold text-green-700">{formatCurrency(data.gross_sales)}</span>
            </div>
          </div>
          
          {/* Deductions */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Platform Deductions</p>
            
            {/* Logistics/Shipping Cost - FIRST deduction (same as shipping collected) */}
            {data.logistics_cost > 0 && (
              <div className="flex items-center justify-between p-2 bg-orange-50 rounded text-sm border border-orange-200">
                <div className="flex flex-col">
                  <span className="text-orange-700">Shipping/Logistics Cost</span>
                  <span className="text-[10px] text-orange-500">Paid to carrier (₹{data.shipping_charge?.toLocaleString()} + ₹{data.shipping_gst?.toLocaleString()} GST)</span>
                </div>
                <span className="text-orange-600 font-medium">-{formatCurrency(data.logistics_cost)}</span>
              </div>
            )}
            
            {/* Net Taxable Sales after logistics */}
            {data.logistics_cost > 0 && (
              <div className="flex items-center justify-between p-2 bg-gray-50 rounded text-xs border border-gray-200">
                <span className="text-gray-600">Net Taxable (for TCS)</span>
                <span className="text-gray-700 font-medium">{formatCurrency(data.net_taxable_sales)}</span>
              </div>
            )}
            
            {/* Commission */}
            <div className="flex items-center justify-between p-2 bg-red-50 rounded text-sm">
              <span className="text-red-700">Commission ({data.commission_rate}%)</span>
              <span className="text-red-600 font-medium">-{formatCurrency(data.commission_amount)}</span>
            </div>
            
            {/* Tax Deductions */}
            {data.tax_deductions?.map((tax, idx) => (
              <div key={tax.id || tax.name || `${tax.title || "tax"}-${idx}`} className="flex items-center justify-between p-2 bg-red-50 rounded text-sm">
                <div className="flex items-center gap-1">
                  <span className="text-red-700">{tax.name} ({tax.rate}%)</span>
                  {tax.section_reference && (
                    <span className="text-[10px] text-gray-500">({tax.section_reference})</span>
                  )}
                </div>
                <span className="text-red-600 font-medium">-{formatCurrency(tax.amount)}</span>
              </div>
            ))}
            
            {/* Gateway Fees */}
            {data.gateway_fee > 0 && (
              <div className="flex items-center justify-between p-2 bg-red-50 rounded text-sm">
                <span className="text-red-700">Payment Gateway</span>
                <span className="text-red-600 font-medium">-{formatCurrency(data.gateway_fee)}</span>
              </div>
            )}
            
            {data.gateway_gst > 0 && (
              <div className="flex items-center justify-between p-2 bg-red-50 rounded text-sm">
                <span className="text-red-700">GST on Gateway (18%)</span>
                <span className="text-red-600 font-medium">-{formatCurrency(data.gateway_gst)}</span>
              </div>
            )}
            
            {/* iter-230bs — Shipping Pool Contribution (Aniyor-managed only) */}
            {data.seller_pool > 0 && (
              <div className="flex items-center justify-between p-2 bg-red-50 rounded text-sm">
                <div>
                  <span className="text-red-700">Shipping Pool ({data.seller_pool_rate || 5}%)</span>
                  <p className="text-[10px] text-red-500">
                    Marketplace shipping subsidy pool — Aniyor-managed only
                  </p>
                </div>
                <span className="text-red-600 font-medium">-{formatCurrency(data.seller_pool)}</span>
              </div>
            )}
            
            {/* Total Deductions */}
            <div className="flex items-center justify-between p-2 bg-red-100 rounded text-sm font-semibold border border-red-200">
              <span className="text-red-800">Total Deductions</span>
              <span className="text-red-700">-{formatCurrency(data.total_deductions)}</span>
            </div>
          </div>
          
          {/* Net Payout */}
          <div className={`p-4 rounded-lg border-2 ${isResident ? 'bg-blue-100 border-blue-300' : 'bg-purple-100 border-purple-300'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-medium ${isResident ? 'text-blue-800' : 'text-purple-800'}`}>Net Payout to Vendor</p>
                <p className={`text-xs ${isResident ? 'text-blue-600' : 'text-purple-600'}`}>
                  Effective deduction: {data.effective_rate}%
                </p>
              </div>
              <p className={`text-2xl font-bold ${isResident ? 'text-blue-700' : 'text-purple-700'}`}>
                {formatCurrency(data.net_payout)}
              </p>
            </div>
          </div>
          
          {/* Vendor GST Liability */}
          {data.vendor_gst_liability && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                Vendor's GST Liability
              </p>
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-sm space-y-2">
                <p className="text-amber-800 text-xs mb-2">
                  Payout includes GST collected from customer. Vendor must remit to government:
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-amber-700">
                    <span className="text-xs text-amber-600">Intra-state:</span>
                    <p className="font-medium">CGST: {formatCurrency(data.vendor_gst_liability.cgst)}</p>
                    <p className="font-medium">SGST: {formatCurrency(data.vendor_gst_liability.sgst)}</p>
                  </div>
                  <div className="text-amber-700">
                    <span className="text-xs text-amber-600">Inter-state:</span>
                    <p className="font-medium">IGST: {formatCurrency(data.vendor_gst_liability.igst)}</p>
                  </div>
                </div>
                <p className="text-[10px] text-amber-600 mt-1 italic">
                  *Actual GST depends on HSN codes. Refer Shopify order for exact split.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">Payout & Tax Logic</h1>
            <p className="text-gray-500 mt-1">Configure tax rules, commission tiers, and payout calculations</p>
          </div>
          <div className="flex items-center gap-2">
            {editMode ? (
              <>
                <Button variant="outline" onClick={() => { setEditMode(false); fetchConfig(); }} data-testid="tax-cancel-btn">
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
                <Button onClick={handleSaveConfig} disabled={saving} data-testid="tax-save-btn">
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Changes
                </Button>
              </>
            ) : (
              <Button onClick={() => setEditMode(true)} data-testid="tax-edit-btn">
                <Edit2 className="h-4 w-4 mr-2" />
                Edit Configuration
              </Button>
            )}
          </div>
        </div>

        {/* Country Badge */}
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-sm py-1 px-3">
            <Globe className="h-3 w-3 mr-2" />
            {config?.country_name || 'India'} ({config?.country_code || 'IN'})
          </Badge>
          <Badge variant="outline" className="text-sm py-1 px-3">
            {config?.currency_symbol || '₹'} {config?.currency || 'INR'}
          </Badge>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid grid-cols-4 w-full max-w-2xl">
            <TabsTrigger value="calculator" className="flex items-center gap-2" data-testid="tax-tab-calculator">
              <Calculator className="h-4 w-4" />
              <span className="hidden sm:inline">Calculator</span>
            </TabsTrigger>
            <TabsTrigger value="taxes" className="flex items-center gap-2" data-testid="tax-tab-taxes">
              <FileText className="h-4 w-4" />
              <span className="hidden sm:inline">Tax Rules</span>
            </TabsTrigger>
            <TabsTrigger value="commission" className="flex items-center gap-2" data-testid="tax-tab-commission">
              <Percent className="h-4 w-4" />
              <span className="hidden sm:inline">Commission</span>
            </TabsTrigger>
            <TabsTrigger value="gateway" className="flex items-center gap-2" data-testid="tax-tab-gateway">
              <CreditCard className="h-4 w-4" />
              <span className="hidden sm:inline">Gateway</span>
            </TabsTrigger>
          </TabsList>

          {/* Calculator Tab */}
          <TabsContent value="calculator" className="space-y-6">
            {/* Calculator Inputs */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Payout Calculator Settings
                </CardTitle>
                <CardDescription>
                  Adjust parameters to see real-time payout calculations for both Resident and Non-Resident vendors
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <div className="space-y-2">
                    <Label>Product Value (incl. GST)</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                      <Input
                        type="number"
                        data-testid="calc-product-value"
                        value={calcProductValue}
                        onChange={(e) => setCalcProductValue(parseFloat(e.target.value) || 0)}
                        className="pl-8"
                        min={0}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Shipping Charge</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                      <Input
                        type="number"
                        data-testid="calc-shipping-charge"
                        value={calcShippingCharge}
                        onChange={(e) => setCalcShippingCharge(parseFloat(e.target.value) || 0)}
                        className="pl-8"
                        min={0}
                        placeholder="0 for digital"
                      />
                    </div>
                    <p className="text-[10px] text-gray-500">+18% GST = Logistics Cost</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      Subscription Plan
                      {commissionSource === 'subscription_plans' && (
                        <Badge variant="outline" className="text-[10px] px-1 py-0 bg-green-50 text-green-700">SSOT</Badge>
                      )}
                    </Label>
                    <Select value={calcPlanCode} onValueChange={setCalcPlanCode}>
                      <SelectTrigger data-testid="calc-plan-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {commissionTiers.map(tier => (
                          <SelectItem key={tier.plan_code} value={tier.plan_code}>
                            {tier.plan_name} ({tier.rate}%)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Gateway Provider</Label>
                    <Select value={calcGatewayProvider} onValueChange={handleProviderChange}>
                      <SelectTrigger data-testid="calc-gateway-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {gatewayProviders.filter(p => p.is_active).map(provider => (
                          <SelectItem key={provider.provider_code} value={provider.provider_code}>
                            {provider.provider_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Payment Method</Label>
                    <Select value={calcPaymentMethod} onValueChange={setCalcPaymentMethod}>
                      <SelectTrigger data-testid="calc-payment-method-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {selectedProviderMethods.filter(m => m.is_active).map(method => (
                          <SelectItem key={method.method} value={method.method}>
                            {method.label} ({method.percent_fee}%)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="space-y-2">
                    <Label>Entity Type</Label>
                    <Select value={calcEntityType} onValueChange={setCalcEntityType}>
                      <SelectTrigger data-testid="calc-entity-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="individual_huf">Individual / HUF</SelectItem>
                        <SelectItem value="company_llp">Company / LLP</SelectItem>
                        <SelectItem value="partnership">Partnership Firm</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {/* iter-230bs — Aniyor-managed vs Self-managed shipping toggle */}
                  <div className="space-y-2">
                    <Label>Shipping Mode</Label>
                    <Select value={calcShippingMode} onValueChange={setCalcShippingMode}>
                      <SelectTrigger data-testid="calc-shipping-mode-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="aniyor">Aniyor-managed (deducted from vendor)</SelectItem>
                        <SelectItem value="self">Self-managed (passed through to vendor)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-gray-500">
                      Aniyor → deduct shipping + GST + 5% pool. Self → vendor keeps shipping + GST in payout.
                    </p>
                  </div>
                </div>
                
                <div className="mt-4 flex justify-end">
                  <Button variant="outline" onClick={runCalculation} disabled={calculating} data-testid="calc-recalculate-btn">
                    {calculating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                    Recalculate
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Side-by-side Calculator Results */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <PayoutCalculatorCard
                title="Resident Vendor"
                data={residentCalc}
                isResident={true}
                icon={User}
              />
              <PayoutCalculatorCard
                title="Non-Resident Vendor"
                data={nonResidentCalc}
                isResident={false}
                icon={Building2}
              />
            </div>

            {/* Info Card */}
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-amber-900">About Resident vs Non-Resident</p>
                    <p className="text-sm text-amber-800 mt-1">
                      <strong>Resident vendors</strong> are subject to all Indian tax laws including TDS u/s 194-O and TCS under GST.
                      <strong> Non-Resident vendors</strong> (NRIs, foreign entities) may have different tax treatments - 
                      some taxes like TCS may not apply, while others may have different rates.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* v2 Estimator — Zone / Weight / Waterfall / Surcharge */}
            <Card data-testid="calc-v2-estimator-wrapper" className="border-indigo-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="h-5 w-5 text-indigo-600" />
                  v2 Payout Estimator — Waterfall + Zone Matrix
                </CardTitle>
                <CardDescription>
                  Uses live rates from the <strong>Tax Rules</strong>, <strong>Commission</strong>,
                  <strong> Gateway</strong>, <strong>Marketplace Rules</strong> and
                  <strong> Route Fee</strong> tabs (single source of truth).  Edit any rate there
                  and re-estimate to see the impact.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <PayoutEstimator compact />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tax Rules Tab */}
          <TabsContent value="taxes" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Tax Rules Configuration</h2>
              {editMode && (
                <Button size="sm" onClick={() => handleEditTaxRule(null, true)} data-testid="tax-add-rule-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Tax Rule
                </Button>
              )}
            </div>
            
            <div className="space-y-3">
              {config?.tax_rules?.map((rule, idx) => (
                <Card key={rule.key || idx} className={!rule.is_active ? 'opacity-60' : ''}>
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold">{rule.name}</h3>
                          {rule.section_reference && (
                            <Badge variant="outline" className="text-xs">{rule.section_reference}</Badge>
                          )}
                          {!rule.is_active && (
                            <Badge variant="secondary" className="text-xs">Inactive</Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{rule.description}</p>
                        <div className="flex flex-wrap gap-4 text-sm">
                          <div>
                            <span className="text-gray-500">Applied on:</span>{' '}
                            <span className="font-medium capitalize">{rule.applied_on?.replace('_', ' ')}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Resident Rate:</span>{' '}
                            <span className="font-medium text-blue-600">{rule.resident_rate}%</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Non-Resident Rate:</span>{' '}
                            <span className="font-medium text-purple-600">{rule.non_resident_rate}%</span>
                          </div>
                        </div>
                        {rule.notes && (
                          <p className="text-xs text-gray-500 mt-2 italic">{rule.notes}</p>
                        )}
                      </div>
                      {editMode && (
                        <div className="flex items-center gap-2 ml-4">
                          <Button size="sm" variant="ghost" onClick={() => handleEditTaxRule(rule)} data-testid={`tax-edit-rule-${rule.key}`}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteTaxRule(rule.key)} data-testid={`tax-delete-rule-${rule.key}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* ───── Marketplace Rules sub-card (Feb-2026 waterfall) ───── */}
            <MarketplaceRulesCard
              config={config}
              editMode={editMode}
              onSave={async (rules) => {
                try {
                  await apiCall(`/api/tax-payout-config/${config.country_code}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ marketplace_rules: rules }),
                  });
                  toast({ title: 'Saved', description: 'Marketplace rules updated' });
                  fetchConfig();
                } catch (e) {
                  toast({ title: 'Error', description: e.message, variant: 'destructive' });
                }
              }}
            />

            {/* ───── Razorpay Route Fee sub-card (payout-time fee) ───── */}
            <RouteFeeCard
              config={config}
              editMode={editMode}
              onSave={async (rf) => {
                try {
                  await apiCall(`/api/tax-payout-config/${config.country_code}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ route_fee: rf }),
                  });
                  toast({ title: 'Saved', description: 'Route fee updated' });
                  fetchConfig();
                } catch (e) {
                  toast({ title: 'Error', description: e.message, variant: 'destructive' });
                }
              }}
            />
          </TabsContent>

          {/* Commission Tab */}
          <TabsContent value="commission" className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">Commission Tiers by Plan</h2>
                <p className="text-sm text-gray-500 flex items-center gap-2 mt-1">
                  {commissionSource === 'subscription_plans' ? (
                    <>
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      <span>Rates synced from <a href="/subscription-plans" className="text-blue-600 hover:underline font-medium">Subscription Plans</a> (Single Source of Truth)</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="h-4 w-4 text-amber-600" />
                      <span>Using default rates. <a href="/subscription-plans" className="text-blue-600 hover:underline">Configure in Subscription Plans</a></span>
                    </>
                  )}
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => window.location.href = '/subscription-plans'} data-testid="tax-manage-plans-btn">
                <Settings className="h-4 w-4 mr-2" />
                Manage Plans
              </Button>
            </div>
            
            <Card>
              <CardContent className="pt-4">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Plan</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Code</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Commission Rate</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {commissionTiers.map((tier, idx) => (
                        <tr key={tier.plan_code || idx} className="border-b last:border-0">
                          <td className="py-3 px-4 font-medium">{tier.plan_name}</td>
                          <td className="py-3 px-4">
                            <code className="bg-gray-100 px-2 py-1 rounded text-sm">{tier.plan_code}</code>
                          </td>
                          <td className="py-3 px-4">
                            <span className="font-semibold text-blue-600">{tier.rate}%</span>
                          </td>
                          <td className="py-3 px-4">
                            {commissionSource === 'subscription_plans' ? (
                              <Badge className="bg-green-100 text-green-800">Subscription Plans</Badge>
                            ) : (
                              <Badge variant="secondary">Default</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {commissionSource === 'subscription_plans' && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm">
                    <Info className="h-4 w-4 text-blue-600 inline mr-2" />
                    <span className="text-blue-800">
                      Commission rates are managed in Subscription Plans. Changes there will automatically reflect in payout calculations.
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Gateway Tab */}
          <TabsContent value="gateway" className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">Gateway Providers</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Configure multiple payment gateways (Razorpay, PhonePe, HDFC, etc.) with their fee structures
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" data-testid="tax-sync-channels-btn" onClick={async () => {
                  try {
                    const result = await apiCall('/api/tax-payout-config/sync-to-sales-channels', { method: 'POST' });
                    toast({ title: 'Success', description: `Synced ${result.total_rules} rules to Sales Channels` });
                  } catch (e) {
                    toast({ title: 'Error', description: 'Failed to sync', variant: 'destructive' });
                  }
                }}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Sync to Sales Channels
                </Button>
                {editMode && (
                  <Button size="sm" data-testid="tax-add-provider-btn" onClick={() => {
                    setEditingItem({
                      provider_code: '',
                      provider_name: '',
                      description: '',
                      gst_applicable: true,
                      currency_conversion_fee: 0,
                      is_active: true,
                      priority: 50,
                      payment_methods: []
                    });
                    setIsNewItem(true);
                    setShowProviderModal(true);
                  }}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Provider
                  </Button>
                )}
              </div>
            </div>
            
            {/* Gateway Providers List */}
            <div className="space-y-4">
              {config?.gateway_providers?.map((provider, idx) => (
                <Card key={provider.provider_code || idx} className={!provider.is_active ? 'opacity-60' : ''}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CardTitle className="text-lg">{provider.provider_name}</CardTitle>
                        <Badge variant="outline" className="text-xs">Priority: {provider.priority}</Badge>
                        {provider.is_active ? (
                          <Badge className="bg-green-100 text-green-800">Active</Badge>
                        ) : (
                          <Badge variant="secondary">Inactive</Badge>
                        )}
                      </div>
                      {editMode && (
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" data-testid={`tax-edit-provider-${provider.provider_code}`} onClick={() => {
                            setEditingItem({ ...provider });
                            setIsNewItem(false);
                            setShowProviderModal(true);
                          }}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                    <CardDescription className="flex gap-4 mt-1">
                      <span>{provider.description}</span>
                      {provider.gst_applicable && <span className="text-green-600">• 18% GST on fees</span>}
                      {provider.currency_conversion_fee > 0 && (
                        <span className="text-amber-600">• {provider.currency_conversion_fee}% FX fee</span>
                      )}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-2 px-3 font-medium text-gray-600">Payment Method</th>
                            <th className="text-left py-2 px-3 font-medium text-gray-600">Percent Fee</th>
                            <th className="text-left py-2 px-3 font-medium text-gray-600">Fixed Fee</th>
                            <th className="text-left py-2 px-3 font-medium text-gray-600">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {provider.payment_methods?.map((method, mIdx) => (
                            <tr key={method.method || mIdx} className="border-b last:border-0">
                              <td className="py-2 px-3 font-medium">{method.label}</td>
                              <td className="py-2 px-3">
                                <span className={`font-semibold ${method.percent_fee === 0 ? 'text-green-600' : 'text-blue-600'}`}>
                                  {method.percent_fee === 0 ? 'FREE' : `${method.percent_fee}%`}
                                </span>
                              </td>
                              <td className="py-2 px-3">
                                {method.fixed_fee > 0 ? `₹${method.fixed_fee}` : '-'}
                              </td>
                              <td className="py-2 px-3">
                                {method.is_active ? (
                                  <Badge className="bg-green-100 text-green-800 text-xs">Active</Badge>
                                ) : (
                                  <Badge variant="secondary" className="text-xs">Inactive</Badge>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            {/* SSOT Info */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-blue-900">Single Source of Truth (SSOT)</p>
                    <p className="text-sm text-blue-800 mt-1">
                      Gateway fees configured here are the master configuration. Use "Sync to Sales Channels" 
                      to push these rates to the Sales Channel Financial Fee Mapping. Channel fees are absorbed 
                      within the platform commission charged on gross amount.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payout Rules */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Payout Rules
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>Hold Period (Days)</Label>
                    <Input
                      type="number"
                      data-testid="payout-hold-period"
                      value={config?.payout_rules?.hold_period_days || 7}
                      onChange={(e) => editMode && setConfig({
                        ...config,
                        payout_rules: { ...config.payout_rules, hold_period_days: parseInt(e.target.value) || 7 }
                      })}
                      disabled={!editMode}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Minimum Threshold (₹)</Label>
                    <Input
                      type="number"
                      data-testid="payout-min-threshold"
                      value={config?.payout_rules?.min_threshold || 1000}
                      onChange={(e) => editMode && setConfig({
                        ...config,
                        payout_rules: { ...config.payout_rules, min_threshold: parseFloat(e.target.value) || 1000 }
                      })}
                      disabled={!editMode}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Max Daily Payout (₹)</Label>
                    <Input
                      type="number"
                      data-testid="payout-max-daily"
                      value={config?.payout_rules?.max_daily_payout || 1000000}
                      onChange={(e) => editMode && setConfig({
                        ...config,
                        payout_rules: { ...config.payout_rules, max_daily_payout: parseFloat(e.target.value) || 1000000 }
                      })}
                      disabled={!editMode}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Processing Days</Label>
                    <Input
                      data-testid="payout-processing-days"
                      value={config?.payout_rules?.processing_days || '2-3'}
                      onChange={(e) => editMode && setConfig({
                        ...config,
                        payout_rules: { ...config.payout_rules, processing_days: e.target.value }
                      })}
                      disabled={!editMode}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Tax Rule Edit Modal */}
      <Dialog open={showTaxRuleModal} onOpenChange={setShowTaxRuleModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isNewItem ? 'Add Tax Rule' : 'Edit Tax Rule'}</DialogTitle>
            <DialogDescription>
              Configure tax deduction rules for vendor payouts
            </DialogDescription>
          </DialogHeader>
          {editingItem && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Key (Unique ID)</Label>
                  <Input
                    value={editingItem.key}
                    onChange={(e) => setEditingItem({ ...editingItem, key: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                    placeholder="tds_194o"
                    disabled={!isNewItem}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    value={editingItem.name}
                    onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                    placeholder="TDS u/s 194-O"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={editingItem.description}
                  onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                  placeholder="Tax Deducted at Source for E-commerce Operators"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Applied On</Label>
                  <Select 
                    value={editingItem.applied_on} 
                    onValueChange={(v) => setEditingItem({ ...editingItem, applied_on: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gross_sales">Gross Sales</SelectItem>
                      <SelectItem value="net_taxable_sales">Net Taxable Sales</SelectItem>
                      <SelectItem value="commission">Commission Amount</SelectItem>
                      <SelectItem value="payout">Net Payout</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Section Reference</Label>
                  <Input
                    value={editingItem.section_reference}
                    onChange={(e) => setEditingItem({ ...editingItem, section_reference: e.target.value })}
                    placeholder="194-O"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Resident Rate (%)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingItem.resident_rate}
                    onChange={(e) => setEditingItem({ ...editingItem, resident_rate: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Non-Resident Rate (%)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingItem.non_resident_rate}
                    onChange={(e) => setEditingItem({ ...editingItem, non_resident_rate: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={editingItem.notes}
                  onChange={(e) => setEditingItem({ ...editingItem, notes: e.target.value })}
                  placeholder="Additional notes about this tax rule"
                />
              </div>
              
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editingItem.is_active}
                    onCheckedChange={(v) => setEditingItem({ ...editingItem, is_active: v })}
                  />
                  <Label>Active</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editingItem.is_deductible}
                    onCheckedChange={(v) => setEditingItem({ ...editingItem, is_deductible: v })}
                  />
                  <Label>Deductible from Payout</Label>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTaxRuleModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveTaxRule}>
              {isNewItem ? 'Add Rule' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Commission Edit Modal */}
      <Dialog open={showCommissionModal} onOpenChange={setShowCommissionModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isNewItem ? 'Add Commission Tier' : 'Edit Commission Tier'}</DialogTitle>
          </DialogHeader>
          {editingItem && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Plan Code</Label>
                  <Input
                    value={editingItem.plan_code}
                    onChange={(e) => setEditingItem({ ...editingItem, plan_code: e.target.value.toLowerCase() })}
                    placeholder="basic"
                    disabled={!isNewItem}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Plan Name</Label>
                  <Input
                    value={editingItem.plan_name}
                    onChange={(e) => setEditingItem({ ...editingItem, plan_name: e.target.value })}
                    placeholder="Basic"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Commission Rate (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={editingItem.rate}
                  onChange={(e) => setEditingItem({ ...editingItem, rate: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={editingItem.is_active}
                  onCheckedChange={(v) => setEditingItem({ ...editingItem, is_active: v })}
                />
                <Label>Active</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCommissionModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveCommission}>
              {isNewItem ? 'Add Tier' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Gateway Edit Modal */}
      <Dialog open={showGatewayModal} onOpenChange={setShowGatewayModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isNewItem ? 'Add Gateway Charge' : 'Edit Gateway Charge'}</DialogTitle>
          </DialogHeader>
          {editingItem && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Method Key</Label>
                  <Input
                    value={editingItem.method}
                    onChange={(e) => setEditingItem({ ...editingItem, method: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                    placeholder="credit_card"
                    disabled={!isNewItem}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Display Label</Label>
                  <Input
                    value={editingItem.label}
                    onChange={(e) => setEditingItem({ ...editingItem, label: e.target.value })}
                    placeholder="Credit Card"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Percent Fee (%)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingItem.percent_fee}
                    onChange={(e) => setEditingItem({ ...editingItem, percent_fee: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fixed Fee (₹)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingItem.fixed_fee}
                    onChange={(e) => setEditingItem({ ...editingItem, fixed_fee: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editingItem.gst_applicable}
                    onCheckedChange={(v) => setEditingItem({ ...editingItem, gst_applicable: v })}
                  />
                  <Label>GST Applicable (18%)</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editingItem.is_active}
                    onCheckedChange={(v) => setEditingItem({ ...editingItem, is_active: v })}
                  />
                  <Label>Active</Label>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGatewayModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveGateway}>
              {isNewItem ? 'Add Gateway' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Gateway Provider Edit Modal */}
      <Dialog open={showProviderModal} onOpenChange={setShowProviderModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isNewItem ? 'Add Gateway Provider' : 'Edit Gateway Provider'}</DialogTitle>
            <DialogDescription>
              Configure the payment gateway provider and its supported payment methods
            </DialogDescription>
          </DialogHeader>
          {editingItem && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Provider Code</Label>
                  <Input
                    value={editingItem.provider_code}
                    onChange={(e) => setEditingItem({ ...editingItem, provider_code: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                    placeholder="razorpay"
                    disabled={!isNewItem}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Provider Name</Label>
                  <Input
                    value={editingItem.provider_name}
                    onChange={(e) => setEditingItem({ ...editingItem, provider_name: e.target.value })}
                    placeholder="Razorpay"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={editingItem.description}
                  onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                  placeholder="India's leading payment gateway"
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Input
                    type="number"
                    value={editingItem.priority}
                    onChange={(e) => setEditingItem({ ...editingItem, priority: parseInt(e.target.value) || 0 })}
                  />
                  <p className="text-xs text-gray-500">Higher = preferred</p>
                </div>
                <div className="space-y-2">
                  <Label>FX Conversion Fee (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={editingItem.currency_conversion_fee}
                    onChange={(e) => setEditingItem({ ...editingItem, currency_conversion_fee: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div className="flex items-end gap-4">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={editingItem.gst_applicable}
                      onCheckedChange={(v) => setEditingItem({ ...editingItem, gst_applicable: v })}
                    />
                    <Label>18% GST on Fees</Label>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Switch
                  checked={editingItem.is_active}
                  onCheckedChange={(v) => setEditingItem({ ...editingItem, is_active: v })}
                />
                <Label>Active</Label>
              </div>
              
              {/* Payment Methods */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base font-semibold">Payment Methods</Label>
                  <Button size="sm" variant="outline" onClick={handleAddPaymentMethod}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add Method
                  </Button>
                </div>
                
                <div className="space-y-3">
                  {editingItem.payment_methods?.map((method, idx) => (
                    <div key={method.id || method.name || `${method.title || "method"}-${idx}`} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                      <div className="grid grid-cols-4 gap-2 flex-1">
                        <Input
                          placeholder="Method code"
                          value={method.method}
                          onChange={(e) => handleUpdatePaymentMethod(idx, 'method', e.target.value.toLowerCase())}
                        />
                        <Input
                          placeholder="Label"
                          value={method.label}
                          onChange={(e) => handleUpdatePaymentMethod(idx, 'label', e.target.value)}
                        />
                        <div className="relative">
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="% Fee"
                            value={method.percent_fee}
                            onChange={(e) => handleUpdatePaymentMethod(idx, 'percent_fee', parseFloat(e.target.value) || 0)}
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">%</span>
                        </div>
                        <div className="relative">
                          <Input
                            type="number"
                            step="1"
                            placeholder="Fixed"
                            value={method.fixed_fee}
                            onChange={(e) => handleUpdatePaymentMethod(idx, 'fixed_fee', parseFloat(e.target.value) || 0)}
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">₹</span>
                        </div>
                      </div>
                      <Switch
                        checked={method.is_active}
                        onCheckedChange={(v) => handleUpdatePaymentMethod(idx, 'is_active', v)}
                      />
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleRemovePaymentMethod(idx)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  
                  {(!editingItem.payment_methods || editingItem.payment_methods.length === 0) && (
                    <p className="text-sm text-gray-500 text-center py-4">
                      No payment methods configured. Click "Add Method" to add one.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProviderModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveProvider}>
              {isNewItem ? 'Add Provider' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};


/** ───────────────────────────────────────────────────────────
 *  Marketplace Rules sub-card (Feb-2026 waterfall)
 *  ─ Shipping GST, Seller Pool (5%), Marketplace Pool (3%)
 *  ────────────────────────────────────────────────────────── */
const MarketplaceRulesCard = ({ config, editMode, onSave }) => {
  const rules = config?.marketplace_rules || {
    shipping_gst_pct: 18, seller_pool_pct: 5, marketplace_pool_pct: 3, is_active: true,
  };
  const [draft, setDraft] = useState(rules);
  useEffect(() => { setDraft(rules); }, [JSON.stringify(rules)]);

  return (
    <Card className="border-indigo-200" data-testid="marketplace-rules-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Percent className="h-5 w-5 text-indigo-600" />
              Marketplace Rules (Feb-2026 Waterfall)
            </CardTitle>
            <CardDescription>
              Aniyor-specific levers used by the payout engine. Edits here
              auto-apply to every future estimate and settlement.
            </CardDescription>
          </div>
          {editMode && (
            <Button size="sm" onClick={() => onSave(draft)}
                    data-testid="mkt-rules-save-btn">
              <Save className="h-4 w-4 mr-1" /> Save
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <RateField
            label="Shipping GST (% on S)"
            value={draft.shipping_gst_pct}
            onChange={(v) => setDraft({ ...draft, shipping_gst_pct: v })}
            editMode={editMode}
            hint="Applied to the shipping base. Default 18 %."
            testid="mkt-shipping-gst"
          />
          <RateField
            label="Seller Pool (% of Product)"
            value={draft.seller_pool_pct}
            onChange={(v) => setDraft({ ...draft, seller_pool_pct: v })}
            editMode={editMode}
            hint="Per-item vendor bump. Capped at Max-Zone-E rate in the engine."
            testid="mkt-seller-pool"
          />
          <RateField
            label="Marketplace Pool (% of Product)"
            value={draft.marketplace_pool_pct}
            onChange={(v) => setDraft({ ...draft, marketplace_pool_pct: v })}
            editMode={editMode}
            hint="Platform allocation used as surcharge threshold. NOT deducted from vendor."
            testid="mkt-marketplace-pool"
          />
        </div>
        <div className="mt-3 p-3 bg-indigo-50 rounded-lg text-xs text-indigo-900 flex items-start gap-2">
          <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
          <span>
            Waterfall order: the 5 % Seller Pool is consumed FIRST by any
            actual-carrier-bill gap, then the 3 % Marketplace Pool, then
            surcharge. The 5 % bump is capped at the Max-Zone-E rate so
            high-value items don't over-bump.
          </span>
        </div>
      </CardContent>
    </Card>
  );
};


/** ───────────────────────────────────────────────────────────
 *  Razorpay Route Fee sub-card (payout-time fee, negotiable)
 *  ────────────────────────────────────────────────────────── */
const RouteFeeCard = ({ config, editMode, onSave }) => {
  const rf = config?.route_fee || {
    provider: 'razorpay_route',
    provider_name: 'Razorpay Route',
    percent_fee: 0.25,
    fixed_fee: 0,
    gst_applicable: false,
    is_active: true,
  };
  const [draft, setDraft] = useState(rf);
  useEffect(() => { setDraft(rf); }, [JSON.stringify(rf)]);

  return (
    <Card className="border-sky-200" data-testid="route-fee-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5 text-sky-600" />
              Razorpay Route Fee (Payout-time)
            </CardTitle>
            <CardDescription>
              Applied on Gross Total at settlement. Separate from the
              payment-collection gateway fee. Negotiable per commercial
              agreement.
            </CardDescription>
          </div>
          {editMode && (
            <Button size="sm" onClick={() => onSave(draft)}
                    data-testid="route-fee-save-btn">
              <Save className="h-4 w-4 mr-1" /> Save
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <RateField
            label="Route Fee (%)"
            value={draft.percent_fee}
            onChange={(v) => setDraft({ ...draft, percent_fee: v })}
            editMode={editMode}
            hint="Current 0.25 %. Editable post-negotiation."
            testid="route-fee-percent"
          />
          <div>
            <Label>Fixed Fee (₹)</Label>
            {editMode ? (
              <Input type="number" min={0} step={0.01}
                     data-testid="route-fee-fixed"
                     value={draft.fixed_fee}
                     onChange={(e) => setDraft({ ...draft, fixed_fee: parseFloat(e.target.value) || 0 })}
              />
            ) : (
              <p className="text-base font-medium mt-2">₹{(draft.fixed_fee || 0).toFixed(2)}</p>
            )}
          </div>
          <div>
            <Label>Status</Label>
            <div className="mt-2 flex items-center gap-2">
              <Switch
                data-testid="route-fee-active"
                checked={draft.is_active}
                onCheckedChange={(v) => editMode && setDraft({ ...draft, is_active: v })}
                disabled={!editMode}
              />
              <span className="text-sm">
                {draft.is_active ? 'Active — deducted at payout' : 'Disabled'}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};


/** Small reusable rate input with % display */
const RateField = ({ label, value, onChange, editMode, hint, testid }) => (
  <div>
    <Label>{label}</Label>
    {editMode ? (
      <div className="relative">
        <Input
          type="number" min={0} step={0.01}
          data-testid={testid}
          value={value ?? 0}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
      </div>
    ) : (
      <p className="text-base font-semibold text-indigo-700 mt-2" data-testid={`${testid}-value`}>
        {(value ?? 0).toFixed(2)}%
      </p>
    )}
    {hint && <p className="text-[11px] text-gray-500 mt-1">{hint}</p>}
  </div>
);


export default TaxAndPayoutLogic;
